import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

/**
 * SlatWipes — every section handover on the homepage, played as a venetian blind.
 *
 * Ported from labeau_landing. While the seam between two sections travels up the
 * viewport, the outgoing section is covered by horizontal slats that open in
 * place until they meet. The slats carry the INCOMING section's ground colour,
 * and the overlay is clipped to whatever is left of the outgoing one — so the
 * slats never paint over the section rising underneath them, and the frame in
 * which they close is the frame in which there is nothing left to cover. Those
 * two being one event is what makes it read as a handoff rather than a dissolve.
 *
 * One overlay and one scroll listener for the whole page, not one per boundary:
 * only one seam can be mid-flight at a time (they are a viewport apart), so a
 * single set of bars gets re-coloured and re-pointed at whichever boundary is
 * live. Per frame this writes one height, N transforms, and a colour only when
 * the seam changes — no layout, no paint.
 *
 * Two Sense's sections alternate cream → ink → white → ink → grey → ink → white,
 * which is exactly the high-contrast rhythm this effect was built for.
 *
 * Measured by element id rather than refs, because the ids live on the
 * LazySection wrappers — those exist (holding their reserved height) even before
 * the section inside them has mounted, so the geometry is stable throughout.
 *
 * Skipped entirely under `prefers-reduced-motion` and on narrow screens, where a
 * full-viewport overlay animating on every scroll frame is a poor trade.
 *
 * @param {Array<{from: string, color: string}>} boundaries
 *        `from` is the outgoing section's id, `color` the incoming ground.
 *        MUST be declared module-level at the call site — a fresh array each
 *        render would re-run the effect every render.
 * @param {number} [count=7]     slats across the viewport
 * @param {number} [stagger=0.45] share of the travel spent fanning them out
 */
const SlatWipes = ({ boundaries, count = 7, stagger = 0.45 }) => {
    const hostRef = useRef(null);
    const barsRef = useRef([]);
    const paintedRef = useRef(null);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;

        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        const narrow = window.matchMedia?.('(max-width: 900px)')?.matches;
        if (reduced || narrow) return undefined;

        let raf = 0;
        const apply = () => {
            raf = 0;
            const vh = window.innerHeight || 1;

            // Whichever seam is currently crossing the viewport. If two ever
            // overlap — a section shorter than the screen — the further-along
            // one is the one the eye is actually on.
            let live = null;
            for (let i = 0; i < boundaries.length; i += 1) {
                const el = document.getElementById(boundaries[i].from);
                if (!el) continue;
                const { bottom } = el.getBoundingClientRect();
                if (bottom <= 0 || bottom >= vh) continue;
                const p = 1 - bottom / vh;
                if (!live || p > live.p) live = { p, bottom, color: boundaries[i].color };
            }

            if (!live) {
                if (host.style.display !== 'none') host.style.display = 'none';
                return;
            }

            // Re-colour only when the seam changes: this is the one paint-bound
            // property here, and it is constant for a whole handover.
            if (paintedRef.current !== live.color) {
                paintedRef.current = live.color;
                for (let i = 0; i < count; i += 1) {
                    const bar = barsRef.current[i];
                    if (bar) bar.style.background = live.color;
                }
            }

            host.style.display = 'block';
            // Clip to what is left of the outgoing section, so the slats never
            // paint over the incoming one rising underneath them.
            host.style.height = `${Math.min(live.bottom, vh)}px`;

            const span = 1 - stagger;
            for (let i = 0; i < count; i += 1) {
                const bar = barsRef.current[i];
                if (!bar) continue;
                // Bottom slats lead, so the blind closes toward the seam the eye
                // is already tracking rather than against it.
                const delay = count > 1 ? stagger * (1 - i / (count - 1)) : 0;
                const q = Math.min(Math.max((live.p - delay) / span, 0), 1);
                const eased = 1 - (1 - q) ** 3;
                bar.style.transform = `scaleY(${eased.toFixed(4)})`;
            }
        };

        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(apply);
        };
        apply();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [boundaries, count, stagger]);

    return (
        <Box
            ref={hostRef}
            aria-hidden
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: 0,
                display: 'none',
                overflow: 'hidden',
                pointerEvents: 'none',
                // Above the sections, below the WhatsApp button (999) and the
                // floating header (MUI AppBar sits at 1100).
                zIndex: 10,
            }}
        >
            {/* Held at a full viewport so the slat pitch stays put while the host
                above it shrinks — the blind opens in place, it does not compress. */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100vh' }}>
                {Array.from({ length: count }, (_, i) => (
                    <Box
                        key={i}
                        ref={(el) => {
                            barsRef.current[i] = el;
                        }}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: `${(i * 100) / count}%`,
                            // The extra pixel closes the subpixel seam between
                            // neighbours once they are all fully open.
                            height: `calc(${100 / count}% + 1px)`,
                            transformOrigin: 'center',
                            transform: 'scaleY(0)',
                            willChange: 'transform',
                            // Reads on light grounds and dark: over cream a slat
                            // needs an edge or the blind is invisible; over the
                            // ink sections it just softens it.
                            boxShadow: '0 8px 26px rgba(0,0,0,0.10), 0 -8px 26px rgba(0,0,0,0.10)',
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default SlatWipes;
